"use client";

import { PlusIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { FOCUS_RING_INSET } from "../focus-ring";
import { HOVER_EASE } from "../states";

// Work OS InlineAdd: a muted "+ Add task" row that turns into an autofocused field. Enter adds
// and keeps the field open for the next one; Escape, or leaving it empty, closes it.

export type InlineAddProps = {
  onAdd: (title: string) => void;
  label?: string;
  /** Accessible name of the field, e.g. "New task in Doing". */
  fieldLabel?: string;
  className?: string;
};

export function InlineAdd({
  onAdd,
  label = "Add task",
  fieldLabel = "New task title",
  className,
}: InlineAddProps) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState("");
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const close = (refocus: boolean) => {
    setOpen(false);
    setValue("");
    if (refocus) requestAnimationFrame(() => buttonRef.current?.focus());
  };
  if (!open)
    return (
      <button
        ref={buttonRef}
        type="button"
        data-slot="inline-add"
        onClick={() => setOpen(true)}
        className={cn(
          "flex h-9 w-full items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground",
          HOVER_EASE,
          FOCUS_RING_INSET,
          className,
        )}
      >
        <PlusIcon aria-hidden={true} className="size-4" />
        {label}
      </button>
    );
  return (
    <input
      data-slot="inline-add"
      // biome-ignore lint/a11y/noAutofocus: opened by the user's own click on "Add task".
      autoFocus
      aria-label={fieldLabel}
      value={value}
      placeholder="Task title — Enter to add, Esc to cancel"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const title = value.trim();
          if (title) {
            onAdd(title);
            setValue("");
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          close(true);
        }
      }}
      onBlur={() => {
        if (!value.trim()) close(false);
      }}
      className={cn(
        "h-9 w-full rounded-md border border-ring bg-card px-2 text-sm text-foreground outline-none ring-2 ring-ring placeholder:text-muted-foreground max-md:text-md",
        className,
      )}
    />
  );
}
